import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  TriangleAlert,
} from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetMany,
  useTranslate,
} from "ra-core";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useCon36 } from "../datos/useFiltroEtapas";
import { useMiRol } from "../datos/useMiRol";
import { esAnaliticaNoDisponible, primerDiaMes } from "../dominio/analitica";
import { claveOrigen } from "../dominio/catalogos";
import {
  embudoConTasas,
  esPanelNoDisponible,
  moverPeriodoPanel,
  ordenarAlertas,
  periodoPanel,
  tasa,
  variacion,
  type AlertaPanel,
  type DatosPanel,
  type VistaPanel,
} from "../dominio/panelInicio";
import type { Origen } from "../dominio/tipos";
import { CampanaAvisos } from "../equipo/CampanaAvisos";
import { BotonGuia } from "../guia/BotonGuia";
import { FiltroPersonasSelect } from "../equipo/FiltroPersonasSelect";
import { useFiltroPersonas } from "../equipo/useFiltroPersonas";
import { formatoImporte } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { EnlacePerfilMovil } from "../layout/EnlacePerfilMovil";
import { MarcaCabecera } from "../layout/MarcaCabecera";
import { ComisionesDelMes } from "./ComisionesDelMes";
import { GraficoActividad, Minilinea } from "./GraficoActividad";
import {
  Canales,
  FiltroFuente,
  MejorHora,
  ObjetivosMes,
  Perdidas,
  Prevision,
  Sectores,
  Velocidad,
  type SumaObjetivos,
} from "./PanelAnalisis";
import { Bloque, Pct, Td, Th } from "./piezas";

const VISTAS: VistaPanel[] = ["dia", "semana", "mes", "trimestre"];
const numero = (n: number) => n.toLocaleString("es-ES");

/**
 * Inicio (45, auditoría y rediseño de Robert, 30/10/2026): cómo va el negocio en un periodo, no
 * cuántas fichas hay. Arriba, periodo y persona; seis cifras comparadas con el periodo anterior;
 * el embudo de los leads que entraron; lo que hay que mirar ahora; la actividad por día; el
 * ranking y, para Jan y Robert, el dinero. 47 (31/10/2026): fuente de los datos, objetivos del
 * mes, dónde se pierde y por qué, canal y sector, velocidad y esfuerzo, mejor hora para llamar
 * y previsión. Todo lo cuenta la base.
 */
export const PanelInicio = ({ alFallar }: { alFallar: ReactNode }) => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const isMobile = useIsMobile();
  const { vende, esConsultor } = useMiRol();
  const con36 = useCon36();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [vista, setVista] = useState<VistaPanel>("mes");
  const [ref, setRef] = useState(() => new Date());
  const [seleccion, setSeleccion] = useFiltroPersonas();
  const personas = useMemo(
    () =>
      esAdmin
        ? seleccion.map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : [],
    [esAdmin, seleccion],
  );
  const { desde, hasta } = periodoPanel(vista, ref);
  const { ajustes } = useAjustes();
  // 47: fuente de los datos (negocios.origen); null = todas.
  const [origen, setOrigen] = useState<string | null>(null);

  const panel = useQuery({
    queryKey: ["panel", desde, hasta, personas, origen],
    queryFn: () => dataProvider.panel(desde, hasta, personas, origen),
    retry: (n, e) => !esPanelNoDisponible(e) && n < 2,
  });
  const analisis = useQuery({
    queryKey: ["panel-analisis", desde, hasta, personas, origen],
    queryFn: () => dataProvider.panelAnalisis(desde, hasta, personas, origen),
    retry: (n, e) => !esAnaliticaNoDisponible(e) && n < 2,
  });
  // Sin la 47, los bloques de la 45 (origen y motivos); mientras carga, nada.
  const sin47 = !!analisis.error && esAnaliticaNoDisponible(analisis.error);
  const a = analisis.data;
  const mes = vista === "mes" ? primerDiaMes(desde) : null;
  const { data: objetivos, error: errorObjetivos } = useGetList<{
    id: number;
    sales_id: number;
    llamadas: number | null;
    citas: number | null;
    clientes: number | null;
    cobrado: number | null;
  }>(
    "objetivos",
    {
      filter: { mes },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: !!mes && !sin47, retry: false },
  );
  const suma = useMemo<SumaObjetivos | null>(() => {
    if (!objetivos?.length) return null;
    const mios = objetivos.filter(
      (o) => !personas.length || personas.includes(Number(o.sales_id)),
    );
    const total = (k: keyof SumaObjetivos) =>
      mios.some((o) => o[k] != null)
        ? mios.reduce((s2, o) => s2 + Number(o[k] ?? 0), 0)
        : null;
    const r = {
      llamadas: total("llamadas"),
      citas: total("citas"),
      clientes: total("clientes"),
      cobrado: total("cobrado"),
    };
    return Object.values(r).some((v) => v) ? r : null;
  }, [objetivos, personas]);
  const alertas = useQuery({
    queryKey: ["panel-alertas", personas],
    queryFn: () => dataProvider.panelAlertas(personas),
    refetchInterval: 60_000,
    retry: (n, e) => !esPanelNoDisponible(e) && n < 2,
  });

  if (panel.error && esPanelNoDisponible(panel.error)) return <>{alFallar}</>;

  const nombre = (identity?.fullName ?? "").split(" ")[0];
  const d = panel.data;

  const cuerpo = (
    <div
      className="mx-auto max-w-[1400px] space-y-6 pb-12"
      data-testid="panel-inicio"
    >
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="etiqueta">
            {translate(
              esAdmin ? "nevora.inicio.eyebrow_admin" : "nevora.inicio.eyebrow",
            )}
          </p>
          <h1 className="titulo-pagina">
            {translate("nevora.inicio.hola", { nombre })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {textoPeriodo(vista, desde, hasta)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-lg bg-foreground/[0.05] p-0.5"
            role="group"
          >
            {VISTAS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  setVista(v);
                  setRef(new Date());
                }}
                aria-pressed={vista === v}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  vista === v
                    ? "bg-card text-foreground shadow-tarjeta"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {translate(`nevora.inicio.vistas.${v}`)}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={translate("nevora.inicio.anterior")}
              onClick={() => setRef(moverPeriodoPanel(vista, ref, -1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={translate("nevora.inicio.siguiente")}
              onClick={() => setRef(moverPeriodoPanel(vista, ref, 1))}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <FiltroFuente valor={origen} cambiar={setOrigen} />
          <FiltroPersonasSelect valor={seleccion} cambiar={setSeleccion} />
          {!isMobile ? <BotonGuia /> : null}
        </div>
      </header>

      <section
        className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6"
        aria-label={translate("nevora.inicio.cifras")}
        data-guia="inicio-cifras"
      >
        {d ? (
          <Cifras datos={d} esAdmin={esAdmin} />
        ) : (
          Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[118px] rounded-xl" />
          ))
        )}
      </section>

      {d && mes && suma ? (
        <Bloque
          titulo={translate("nevora.inicio.objetivos.titulo")}
          ayuda={translate("nevora.inicio.objetivos.ayuda")}
        >
          <ObjetivosMes
            objetivos={suma}
            actual={d.kpis.actual}
            mes={mes}
            esAdmin={esAdmin}
          />
        </Bloque>
      ) : d && mes && esAdmin && !errorObjetivos && objetivos && !sin47 ? (
        <p className="text-xs text-muted-foreground">
          <Link
            to="/equipo"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {translate("nevora.inicio.objetivos.poner")}
          </Link>
        </p>
      ) : null}

      {/* 40: setters y consultores ven aquí sus comisiones del mes. */}
      {!esAdmin && (vende || esConsultor) && con36 ? (
        <ComisionesDelMes />
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Bloque
          titulo={translate("nevora.inicio.embudo.titulo")}
          ayuda={translate("nevora.inicio.embudo.ayuda")}
        >
          {d ? <Embudo datos={d} /> : <Skeleton className="h-64" />}
        </Bloque>
        <Bloque
          titulo={translate("nevora.inicio.alertas.titulo")}
          ayuda={translate("nevora.inicio.alertas.ayuda")}
          guia="inicio-alertas"
        >
          {alertas.data ? (
            <Alertas alertas={alertas.data} />
          ) : (
            <Skeleton className="h-64" />
          )}
        </Bloque>
      </div>

      <Bloque titulo={translate("nevora.inicio.grafico.titulo")}>
        {d ? (
          <GraficoActividad serie={d.serie} />
        ) : (
          <Skeleton className="h-56" />
        )}
      </Bloque>

      {a ? (
        <>
          <Bloque
            titulo={translate("nevora.inicio.perdidas.titulo")}
            ayuda={translate("nevora.inicio.perdidas.ayuda")}
            testid="bloque-perdidas"
          >
            <Perdidas datos={a} />
          </Bloque>
          <div className="grid gap-6 xl:grid-cols-2">
            <Bloque
              titulo={translate("nevora.inicio.canales.titulo")}
              ayuda={translate("nevora.inicio.canales.ayuda")}
            >
              <Canales datos={a} />
            </Bloque>
            <Bloque
              titulo={translate("nevora.inicio.sectores.titulo")}
              ayuda={translate("nevora.inicio.sectores.ayuda")}
            >
              <Sectores datos={a} />
            </Bloque>
          </div>
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            <Bloque
              titulo={translate("nevora.inicio.velocidad.titulo")}
              ayuda={translate("nevora.inicio.velocidad.ayuda")}
            >
              <Velocidad datos={a} esAdmin={esAdmin} />
            </Bloque>
            <Bloque
              titulo={translate("nevora.inicio.franjas.titulo")}
              ayuda={translate("nevora.inicio.franjas.ayuda")}
            >
              <MejorHora datos={a} />
            </Bloque>
          </div>
        </>
      ) : analisis.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : null}

      {d && esAdmin ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <Bloque titulo={translate("nevora.inicio.ranking.titulo")}>
            <Ranking datos={d} />
          </Bloque>
          <Bloque titulo={translate("nevora.inicio.consultores.titulo")}>
            <Consultores datos={d} />
          </Bloque>
        </div>
      ) : null}

      {d && sin47 ? (
        <div className="grid gap-6 xl:grid-cols-3">
          <Bloque
            titulo={translate("nevora.inicio.origenes.titulo")}
            ayuda={translate("nevora.inicio.origenes.ayuda")}
            className="xl:col-span-2"
          >
            <Origenes datos={d} />
          </Bloque>
          <Bloque titulo={translate("nevora.inicio.motivos.titulo")}>
            <Motivos datos={d} />
          </Bloque>
        </div>
      ) : null}

      {d?.dinero || a?.prevision ? (
        <div className="grid gap-6 xl:grid-cols-2">
          {d?.dinero ? (
            <Bloque titulo={translate("nevora.inicio.dinero.titulo")}>
              <Dinero dinero={d.dinero} />
            </Bloque>
          ) : null}
          {a?.prevision ? (
            <Bloque
              titulo={translate("nevora.inicio.prevision.titulo")}
              ayuda={translate("nevora.inicio.prevision.ayuda")}
            >
              <Prevision
                filas={a.prevision}
                probabilidades={ajustes.probabilidades}
              />
            </Bloque>
          ) : null}
        </div>
      ) : null}

      <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {!sin47 ? (
          <Link
            to="/informe"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {translate("nevora.informe.titulo")}
          </Link>
        ) : null}
        <Link
          to="/resumen"
          className="underline underline-offset-2 hover:text-foreground"
        >
          {translate("nevora.resumen.titulo")}
        </Link>
      </p>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MarcaCabecera />
          <div className="ml-auto flex items-center gap-1">
            <BotonGuia soloIcono />
            <CampanaAvisos plegada alinear="end" />
            <EnlacePerfilMovil />
          </div>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return <div className="mt-2">{cuerpo}</div>;
};

const textoPeriodo = (vista: VistaPanel, desde: string, hasta: string) => {
  const f = (s: string, op: Intl.DateTimeFormatOptions) =>
    new Date(`${s}T12:00:00`).toLocaleDateString("es-ES", op);
  if (vista === "dia")
    return f(desde, { weekday: "long", day: "numeric", month: "long" });
  if (vista === "mes") return f(desde, { month: "long", year: "numeric" });
  return `${f(desde, { day: "numeric", month: "short" })} – ${f(hasta, { day: "numeric", month: "short", year: "numeric" })}`;
};

const Variacion = ({ v }: { v: number | null }) => {
  const translate = useTranslate();
  if (v == null)
    return (
      <span className="text-xs text-muted-foreground">
        {translate("nevora.inicio.sin_comparar")}
      </span>
    );
  const sube = v >= 0;
  const Icono = sube ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        sube
          ? "text-emerald-700 dark:text-emerald-400"
          : "text-red-700 dark:text-red-400",
      )}
      title={translate("nevora.inicio.frente_anterior")}
    >
      <Icono className="size-3.5" aria-hidden />
      {sube ? "+" : ""}
      {v} %
    </span>
  );
};

const Cifra = ({
  etiqueta,
  valor,
  detalle,
  v,
  serie,
}: {
  etiqueta: string;
  valor: string;
  detalle?: string | null;
  v: number | null;
  serie?: number[];
}) => (
  <div className="superficie flex flex-col justify-between gap-3 p-4">
    {/* Dos líneas siempre: así las cifras quedan alineadas aunque la etiqueta sea larga. */}
    <p className="etiqueta min-h-[2.4em] leading-[1.2]">{etiqueta}</p>
    <div className="flex items-end justify-between gap-2">
      <p className="cifra-grande text-[2rem] leading-none">{valor}</p>
      {serie ? <Minilinea valores={serie} /> : null}
    </div>
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
      <Variacion v={v} />
      {detalle ? (
        <span className="text-xs text-muted-foreground">{detalle}</span>
      ) : null}
    </div>
  </div>
);

const Cifras = ({
  datos,
  esAdmin,
}: {
  datos: DatosPanel;
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  const a = datos.kpis.actual;
  const p = datos.kpis.anterior;
  const t = (k: string, o?: object) => translate(`nevora.inicio.kpis.${k}`, o);
  const pct = (n: number | null, k: string) => (n == null ? null : t(k, { n }));
  return (
    <>
      <Cifra
        etiqueta={t("llamadas")}
        valor={numero(a.llamadas)}
        detalle={pct(tasa(a.contestadas, a.llamadas), "contestadas")}
        v={variacion(a.llamadas, p.llamadas)}
        serie={datos.serie.map((s) => s.llamadas)}
      />
      <Cifra
        etiqueta={t("gestionados")}
        valor={numero(a.gestionados)}
        detalle={pct(tasa(a.interesados, a.gestionados), "interesados")}
        v={variacion(a.gestionados, p.gestionados)}
      />
      <Cifra
        etiqueta={t("citas")}
        valor={numero(a.citas)}
        v={variacion(a.citas, p.citas)}
        serie={datos.serie.map((s) => s.citas)}
      />
      <Cifra
        etiqueta={t("realizadas")}
        valor={numero(a.realizadas)}
        detalle={pct(
          tasa(a.realizadas, a.realizadas + a.no_shows),
          "asistencia",
        )}
        v={variacion(a.realizadas, p.realizadas)}
      />
      <Cifra
        etiqueta={t("clientes")}
        valor={numero(a.clientes)}
        detalle={pct(tasa(a.clientes, a.realizadas), "cierre")}
        v={variacion(a.clientes, p.clientes, 3)}
      />
      {esAdmin && a.cobrado != null ? (
        <Cifra
          etiqueta={t("cobrado")}
          valor={formatoImporte(a.cobrado)}
          v={variacion(a.cobrado, p.cobrado, 1)}
        />
      ) : (
        <Cifra
          etiqueta={t("propuestas")}
          valor={numero(a.propuestas)}
          v={variacion(a.propuestas, p.propuestas, 3)}
        />
      )}
    </>
  );
};

const Embudo = ({ datos }: { datos: DatosPanel }) => {
  const translate = useTranslate();
  const { pasos, peor } = embudoConTasas(datos.embudo);
  const total = pasos[0]?.n ?? 0;
  if (!total)
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {translate("nevora.inicio.embudo.vacio")}
      </p>
    );
  return (
    <ol className="space-y-2.5">
      {pasos.map((p) => (
        <li
          key={p.paso}
          className="grid grid-cols-[9.5rem_1fr_auto] items-center gap-3"
        >
          <span className="truncate text-sm">
            {translate(`nevora.inicio.embudo.pasos.${p.paso}`)}
          </span>
          <span className="relative h-7 overflow-hidden rounded-md bg-foreground/[0.04]">
            <span
              className={cn(
                "absolute inset-y-0 left-0 rounded-md",
                p.paso === peor
                  ? "bg-amber-500/80"
                  : "bg-[var(--serie-llamadas)]",
              )}
              style={{
                width: `${Math.max((p.n / total) * 100, p.n ? 1.5 : 0)}%`,
              }}
            />
          </span>
          <span className="flex w-28 items-baseline justify-end gap-2 tabular-nums">
            <span className="text-sm font-medium">{numero(p.n)}</span>
            <span className="w-12 text-right text-xs text-muted-foreground">
              {p.delAnterior == null ? "" : `${p.delAnterior} %`}
            </span>
          </span>
        </li>
      ))}
      {peor ? (
        <li className="flex items-center gap-2 pt-1 text-xs text-amber-800 dark:text-amber-300">
          <TriangleAlert className="size-3.5" aria-hidden />
          {translate("nevora.inicio.embudo.se_pierde", {
            paso: translate(`nevora.inicio.embudo.pasos.${peor}`),
          })}
        </li>
      ) : null}
    </ol>
  );
};

const Alertas = ({ alertas }: { alertas: AlertaPanel[] }) => {
  const translate = useTranslate();
  const [abierta, setAbierta] = useState<string | null>(null);
  const lista = ordenarAlertas(alertas);
  if (!lista.length)
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {translate("nevora.inicio.alertas.vacio")}
      </p>
    );
  return (
    <ul className="divide-y divide-border/60">
      {lista.map((a) => (
        <li key={a.tipo} className="py-2">
          {a.tipo === "sin_asignar" ? (
            <Link
              to="/sin-asignar"
              className="flex w-full items-center justify-between gap-3 rounded-md px-1 py-1 text-sm hover:bg-foreground/[0.04]"
            >
              <span>{translate(`nevora.inicio.alertas.tipos.${a.tipo}`)}</span>
              <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-xs font-medium tabular-nums">
                {a.n}
              </span>
            </Link>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setAbierta(abierta === a.tipo ? null : a.tipo)}
                aria-expanded={abierta === a.tipo}
                className="flex w-full items-center justify-between gap-3 rounded-md px-1 py-1 text-left text-sm hover:bg-foreground/[0.04]"
              >
                <span>
                  {translate(`nevora.inicio.alertas.tipos.${a.tipo}`)}
                </span>
                <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-xs font-medium tabular-nums">
                  {a.n}
                </span>
              </button>
              {abierta === a.tipo ? <FichasAlerta ids={a.negocios} /> : null}
            </>
          )}
        </li>
      ))}
    </ul>
  );
};

const FichasAlerta = ({ ids }: { ids: number[] }) => {
  const abrirFicha = useAbrirFicha();
  const { data } = useGetMany<{ id: number; nombre: string }>("negocios", {
    ids,
  });
  return (
    <ul className="mt-1 space-y-0.5 pl-2">
      {(data ?? []).map((n) => (
        <li key={n.id}>
          <button
            type="button"
            onClick={() => abrirFicha(n.id)}
            className="w-full truncate rounded px-1.5 py-0.5 text-left text-sm text-[#336394] hover:bg-foreground/[0.04] dark:text-[#6FA0D6]"
          >
            {n.nombre}
          </button>
        </li>
      ))}
    </ul>
  );
};

const Ranking = ({ datos }: { datos: DatosPanel }) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.inicio.ranking.${k}`);
  const filas = [...datos.ranking].sort(
    (a, b) => b.citas - a.citas || b.llamadas - a.llamadas,
  );
  if (!filas.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr>
            <Th>{t("persona")}</Th>
            <Th derecha>{t("llamadas")}</Th>
            <Th derecha>{t("contestadas")}</Th>
            <Th derecha>{t("citas")}</Th>
            <Th derecha>{t("hechas")}</Th>
            <Th derecha>{t("clientes")}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {filas.map((r) => (
            <tr key={r.sale_id}>
              <Td>{r.nombre}</Td>
              <Td derecha>{numero(r.llamadas)}</Td>
              <Td derecha>
                {tasa(r.contestadas, r.llamadas) == null
                  ? "—"
                  : `${tasa(r.contestadas, r.llamadas)} %`}
              </Td>
              <Td derecha>{numero(r.citas)}</Td>
              <Td derecha>{numero(r.realizadas)}</Td>
              <Td derecha>{numero(r.clientes)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Consultores = ({ datos }: { datos: DatosPanel }) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.inicio.consultores.${k}`);
  if (!datos.consultores.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <table className="w-full text-left">
      <thead>
        <tr>
          <Th>{t("persona")}</Th>
          <Th derecha>{t("hechas")}</Th>
          <Th derecha>{t("asistencia")}</Th>
          <Th derecha>{t("clientes")}</Th>
          <Th derecha>{t("cierre")}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border/60">
        {datos.consultores.map((c) => {
          const asis = tasa(c.hechas, c.hechas + c.no_shows);
          const cierre = tasa(c.clientes, c.hechas);
          return (
            <tr key={c.sale_id}>
              <Td>{c.nombre}</Td>
              <Td derecha>{numero(c.hechas)}</Td>
              <Td derecha>{asis == null ? "—" : `${asis} %`}</Td>
              <Td derecha>{numero(c.clientes)}</Td>
              <Td derecha>{cierre == null ? "—" : `${cierre} %`}</Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/** Conversión por origen: solo como %, nunca como recuento suelto (auditoría 30/10/2026). */
const Origenes = ({ datos }: { datos: DatosPanel }) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.inicio.origenes.${k}`);
  if (!datos.origenes.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr>
            <Th>{t("origen")}</Th>
            <Th derecha>{t("entrados")}</Th>
            <Th derecha>{t("contactados")}</Th>
            <Th derecha>{t("cita")}</Th>
            <Th derecha>{t("cliente")}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {datos.origenes.map((o) => (
            <tr
              key={o.origen}
              className={cn(o.entrados < 20 && "text-muted-foreground")}
            >
              <Td>{translate(claveOrigen(o.origen as Origen))}</Td>
              <Td derecha>{numero(o.entrados)}</Td>
              <Td derecha>
                <Pct a={o.contactados} b={o.entrados} />
              </Td>
              <Td derecha>
                <Pct a={o.citas} b={o.entrados} />
              </Td>
              <Td derecha>
                <Pct a={o.clientes} b={o.entrados} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Motivos = ({ datos }: { datos: DatosPanel }) => {
  const translate = useTranslate();
  if (!datos.motivos.length)
    return (
      <p className="text-sm text-muted-foreground">
        {translate("nevora.inicio.motivos.vacio")}
      </p>
    );
  const max = Math.max(...datos.motivos.map((m) => m.n));
  return (
    <ul className="space-y-2">
      {datos.motivos.slice(0, 8).map((m) => (
        <li key={m.motivo} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{m.motivo}</span>
            <span className="tabular-nums text-muted-foreground">{m.n}</span>
          </div>
          <span className="block h-1.5 overflow-hidden rounded-full bg-foreground/[0.05]">
            <span
              className="block h-full rounded-full bg-foreground/40"
              style={{ width: `${(m.n / max) * 100}%` }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
};

const Dinero = ({ dinero }: { dinero: NonNullable<DatosPanel["dinero"]> }) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.inicio.dinero.${k}`);
  const celdas: [string, number][] = [
    [t("firmado"), dinero.firmado],
    [t("cobrado"), dinero.cobrado],
    [t("pendiente_cobro"), dinero.pendiente_cobro],
    [t("comisiones_pendientes"), dinero.comisiones_pendientes],
  ];
  return (
    <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {celdas.map(([k, v]) => (
        <div key={k} className="space-y-1">
          <dt className="etiqueta">{k}</dt>
          <dd className="cifra-grande text-2xl">{formatoImporte(v)}</dd>
        </div>
      ))}
    </dl>
  );
};
